import * as React from "react";
import {
  Card,
  Text,
  Badge,
  makeStyles,
  tokens,
  Caption1,
  Tooltip,
} from "@fluentui/react-components";
import {
  Circle24Regular,
  ArrowSync24Regular,
  ErrorCircle24Regular,
  CheckmarkCircle24Regular,
  Bot24Regular,
} from "@fluentui/react-icons";
import { TaskDto } from "../../../services/tasks.service";
import {
  normalizeTaskStatus,
  getTaskStatusColor,
} from "../../../utils/taskStatusHelpers";
import { useTypographyStyles } from "../../../styles/typography.styles";

const useStyles = makeStyles({
  card: {
    marginBottom: tokens.spacingVerticalS,
    cursor: "pointer",
    transition: "all 0.2s ease",
    ":hover": {
      boxShadow: tokens.shadow8,
    },
  },
  cardContent: {
    padding: tokens.spacingVerticalS,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXXS,
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
  },
  typeIcon: {
    fontSize: tokens.fontSizeBase500,
    flexShrink: 0,
    color: "#0078D4",
  },
  title: {
    flex: 1,
  },
  footer: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalM,
    flexWrap: "wrap",
  },
  footerItem: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
    color: tokens.colorNeutralForeground3,
  },
  statusIcon: {
    fontSize: tokens.fontSizeBase500,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
  },
});

interface TaskCardProps {
  task: TaskDto;
  onClick: (task: TaskDto) => void;
}

/**
 * Get status display information (label, color, and icon)
 */
const getStatusInfo = (
  status?: string
): { label: string; color: string; icon: React.ComponentType } => {
  const normalizedStatus = normalizeTaskStatus(status);
  const color = getTaskStatusColor(status);
  const statusLower = status?.toLowerCase() || "to_do";

  switch (statusLower) {
    case "to_do":
      return { label: normalizedStatus, color, icon: Circle24Regular };
    case "in_progress":
      return { label: normalizedStatus, color, icon: ArrowSync24Regular };
    case "incomplete":
      return { label: normalizedStatus, color, icon: ErrorCircle24Regular };
    case "complete":
      return { label: normalizedStatus, color, icon: CheckmarkCircle24Regular };
    default:
      return { label: normalizedStatus, color, icon: Circle24Regular };
  }
};

// Helper to format date
const formatDate = (dateString?: string): string => {
  if (!dateString) return "";

  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 60) {
    return diffMins === 1 ? "1 minute ago" : `${diffMins} minutes ago`;
  } else if (diffHours < 24) {
    return diffHours === 1 ? "1 hour ago" : `${diffHours} hours ago`;
  } else if (diffDays < 7) {
    return diffDays === 1 ? "1 day ago" : `${diffDays} days ago`;
  } else {
    return date.toLocaleDateString();
  }
};

const TaskCard: React.FC<TaskCardProps> = ({ task, onClick }) => {
  const styles = useStyles();
  const typography = useTypographyStyles();
  const statusInfo = getStatusInfo(task.status);
  const statusColor = getTaskStatusColor(task.status);

  const handleClick = () => {
    onClick(task);
  };

  return (
    <Card
      className={styles.card}
      onClick={handleClick}
      style={{
        borderLeft: `4px solid ${statusColor}`,
        borderTop: `1px solid ${statusColor}20`,
        borderRight: `1px solid ${statusColor}20`,
        borderBottom: `1px solid ${statusColor}20`,
      }}
    >
      <div className={styles.cardContent}>
        <div className={styles.cardHeader}>
          <div className={styles.typeIcon}>
            <Bot24Regular />
          </div>
          <Text className={`${typography.formLabel} ${styles.title}`}>{task.name}</Text>
          {/* Output-type and task-type badges hidden for now. Original markup
              kept here so we can re-enable when we're ready to surface them again.
              task_type duplicates the task name (it's now the definition's display
              name), so re-enabling that one needs more thought than just uncommenting.

              {task.outputType && (
                <Badge appearance="outline">{task.outputType}</Badge>
              )}
              {task.taskType && (
                <Badge appearance="tint">{task.taskType}</Badge>
              )}
          */}
          <Tooltip content={statusInfo.label} relationship="description">
            <div
              className={styles.statusIcon}
              style={{ color: statusInfo.color }}
            >
              {React.createElement(statusInfo.icon)}
            </div>
          </Tooltip>
        </div>
        <div className={styles.footer}>
          {task.createdByName && (
            <Caption1 className={styles.footerItem}>
              {task.createdByName}
            </Caption1>
          )}
          {task.createdAt && (
            <Caption1 className={styles.footerItem}>
              {formatDate(task.createdAt)}
            </Caption1>
          )}
        </div>
      </div>
    </Card>
  );
};

export default TaskCard;
